import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { 
  Menu, 
  X, 
  Phone, 
  MapPin, 
  Clock, 
  CheckCircle2, 
  HeartPulse, 
  Activity,
  Brain,
  Baby,
  Apple,
  Stethoscope,
  Users,
  Sparkles,
  Scale,
  MessageCircle,
  Instagram,
  ChevronRight,
  ChevronDown,
  Bone,
  Smile,
  Heart,
  Dumbbell,
  PersonStanding,
  Utensils,
  Mic2,
  Hand,
  BriefcaseMedical,
  ArrowRight
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_377cb5a4-ebb3-4ac1-9f15-14a6a5a2575e/artifacts/9pekirpf_Logo%20BeOK_Alta%20Calidad.png";
const WHATSAPP_NUMBER = "56956755142";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=Hola,%20me%20gustaría%20agendar%20una%20evaluación`;
const BOOKING_LINK = "https://agendamiento.reservo.cl/makereserva/agenda/b0sagLK0J06Pgk7y8Z81quT9u3v0Wn";
const INSTAGRAM_URL = "https://www.instagram.com/be.ok.salud/";
const MAPS_EMBED_URL = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3301.624765952866!2d-70.74239892346618!3d-34.15598697311785!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9663436065116743%3A0x6336421064366914!2sBe%20Ok%20Salud%20Integral!5e0!3m2!1ses!2scl!4v1716380000000!5m2!1ses!2scl";

// Custom hook for scroll animations
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [ref, isVisible] = useScrollAnimation();
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (isVisible && !hasAnimated.current) {
      hasAnimated.current = true;
      let startTime;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Problems data
const problems = [
  { icon: Brain, title: "Dolor de cabeza frecuente" },
  { icon: Activity, title: "Dolor cervical y de espalda" },
  { icon: Smile, title: "Bruxismo y problemas mandibulares" },
  { icon: Bone, title: "Dolor lumbar" },
  { icon: Dumbbell, title: "Lesiones musculares" },
  { icon: HeartPulse, title: "Dolor crónico persistente" },
  { icon: Scale, title: "Problemas metabólicos" },
  { icon: Baby, title: "Problemas de desarrollo infantil" }
];

// Services data
const services = [
  { icon: Bone, title: "Kinesiología Traumatológica", desc: "Recuperación de lesiones y dolor musculoesquelético" },
  { icon: Smile, title: "Kinesiología Maxilofacial", desc: "Tratamiento de bruxismo y disfunción temporomandibular" },
  { icon: Heart, title: "Kinesiología Piso Pélvico", desc: "Rehabilitación del suelo pélvico" },
  { icon: Sparkles, title: "Kinesiología Estética", desc: "Tratamientos dermatofuncionales" },
  { icon: Baby, title: "Kinesiología Neuro Infantil", desc: "Desarrollo neuromotor en niños" },
  { icon: Apple, title: "Nutrición Clínica", desc: "Planes nutricionales personalizados" },
  { icon: Mic2, title: "Fonoaudiología Infantil", desc: "Desarrollo del lenguaje y comunicación" },
  { icon: Hand, title: "Terapia Ocupacional Infantil", desc: "Desarrollo de habilidades funcionales" },
  { icon: Stethoscope, title: "Medicina General", desc: "Atención médica integral" },
  { icon: PersonStanding, title: "Matronería", desc: "Cuidado de la salud femenina" },
  { icon: Activity, title: "Evaluación Corporal InBody", desc: "Análisis de composición corporal" }
];

// Team specialties
const teamSpecialties = [
  { specialty: "Kinesiología Traumatológica", icon: Bone },
  { specialty: "Kinesiología Maxilofacial", icon: Smile },
  { specialty: "Kinesiología Piso Pélvico", icon: Heart },
  { specialty: "Kinesiología Embarazo y Post Parto", icon: PersonStanding },
  { specialty: "Kinesiología Neuro Infantil", icon: Baby },
  { specialty: "Kinesiología Dermatofuncional", icon: Sparkles },
  { specialty: "Nutrición Clínica", icon: Apple },
  { specialty: "Nutrición Deportiva", icon: Dumbbell },
  { specialty: "Medicina General", icon: Stethoscope },
  { specialty: "Fonoaudiología Neuro Infantil", icon: Mic2 },
  { specialty: "Terapia Ocupacional Neuro Infantil", icon: Hand }
];

// Why choose us
const whyChooseUs = [
  "Evaluación clínica profunda",
  "Atención personalizada",
  "Equipo multidisciplinario",
  "Tratamientos basados en evidencia",
  "Seguimiento profesional"
];

// Stats data
const stats = [
  { number: 11, suffix: "+", label: "Especialidades" },
  { number: 1000, suffix: "+", label: "Pacientes atendidos" },
  { number: 5, suffix: "+", label: "Años de experiencia" },
  { number: 98, suffix: "%", label: "Satisfacción" }
];

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#servicios", label: "Servicios" },
    { href: "#equipo", label: "Equipo" },
    { href: "#ubicacion", label: "Ubicación" },
    { href: "#contacto", label: "Contacto" }
  ];

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "bg-white/95 backdrop-blur-md shadow-lg py-2" : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href="#" className="flex items-center group" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Be Ok Salud Integral" 
              className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="nav-link text-sm font-medium relative overflow-hidden group"
              >
                <span className="relative z-10">{link.label}</span>
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#2DD4BF] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </a>
            ))}
            <a
              href={BOOKING_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm flex items-center gap-2 group"
              data-testid="header-cta"
            >
              <Phone className="w-4 h-4 transition-transform group-hover:rotate-12" />
              <span>Agendar Evaluación</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[#1E4B6E] relative"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            <div className={`transition-all duration-300 ${isMobileMenuOpen ? 'rotate-180' : ''}`}>
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed inset-0 top-20 bg-white z-40 transform transition-all duration-500 ease-out ${
          isMobileMenuOpen ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
        }`}
        data-testid="mobile-menu"
      >
        <nav className="flex flex-col p-6 gap-2">
          {navLinks.map((link, index) => (
            <a
              key={link.href}
              href={link.href}
              className="text-lg font-medium text-[#1E4B6E] py-4 border-b border-slate-100 transform transition-all duration-300 hover:translate-x-2 hover:text-[#2DD4BF]"
              style={{ transitionDelay: `${index * 50}ms` }}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <a
            href={BOOKING_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-center mt-6 flex items-center justify-center gap-2 animate-pulse-subtle"
          >
            <Phone className="w-5 h-5" />
            Agendar Evaluación
          </a>
        </nav>
      </div>
    </header>
  );
};

// Hero Section with typing effect
const HeroSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollIndicator(window.scrollY < 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section 
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center pt-20 pb-16 overflow-hidden hero-bg"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#2DD4BF]/10 rounded-full blur-3xl animate-float" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#1E4B6E]/5 rounded-full blur-3xl animate-float-delayed" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-[#2DD4BF]/5 to-transparent rounded-full animate-pulse-slow" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Main Headline */}
          <h1 
            className={`text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E4B6E] tracking-tight leading-tight mb-6 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            ¿Dolor crónico, lesiones o problemas de salud que no logran mejorar?
          </h1>

          {/* Subheadline */}
          <p className={`text-lg sm:text-xl text-slate-600 mb-10 max-w-3xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            En Be Ok Salud abordamos la causa del problema con un equipo multidisciplinario especializado en rehabilitación, dolor crónico y salud integral.
          </p>

          {/* CTA Buttons */}
          <div className={`flex flex-col sm:flex-row gap-4 justify-center mb-12 transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <a
              href={BOOKING_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-lg flex items-center justify-center gap-2 group magnetic-btn"
              data-testid="hero-cta-primary"
            >
              <Phone className="w-5 h-5 transition-all group-hover:rotate-12 group-hover:scale-110" />
              <span>Agendar Evaluación</span>
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-2" />
            </a>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary text-lg flex items-center justify-center gap-2 group"
              data-testid="hero-cta-whatsapp"
            >
              <MessageCircle className="w-5 h-5 transition-all group-hover:scale-110" />
              <span>Contactar por WhatsApp</span>
            </a>
          </div>

          {/* Trust Badges */}
          <div className={`flex flex-wrap justify-center gap-4 transition-all duration-1000 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            {[
              "Profesionales especializados",
              "Atención personalizada",
              "Tratamientos basados en evidencia"
            ].map((badge, index) => (
              <span 
                key={index}
                className="trust-badge group cursor-default"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CheckCircle2 className="w-4 h-4 text-[#2DD4BF] transition-transform group-hover:scale-125 group-hover:rotate-12" />
                <span className="transition-colors group-hover:text-[#1E4B6E]">{badge}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-all duration-500 ${
        showScrollIndicator ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}>
        <a href="#problemas" className="flex flex-col items-center text-[#1E4B6E]/60 hover:text-[#2DD4BF] transition-colors">
          <span className="text-sm mb-2">Descubre más</span>
          <ChevronDown className="w-6 h-6 animate-bounce" />
        </a>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section ref={ref} className="py-16 bg-[#1E4B6E] relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-[#1E4B6E] via-[#2a5f87] to-[#1E4B6E] opacity-50" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className={`text-center transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="text-4xl md:text-5xl font-bold text-[#2DD4BF] mb-2">
                <AnimatedCounter end={stat.number} suffix={stat.suffix} />
              </div>
              <div className="text-white/80 text-sm md:text-base">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Problems Section
const ProblemsSection = () => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section id="problemas" data-testid="problems-section" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto" ref={ref}>
        <div className={`text-center mb-16 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1E4B6E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Problemas que tratamos
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Nuestro equipo evalúa cada caso para diseñar un tratamiento personalizado enfocado en resolver la causa del problema.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {problems.map((problem, index) => {
            const IconComponent = problem.icon;
            return (
              <div
                key={index}
                className={`problem-card bg-[#F0F9FA] p-6 rounded-2xl text-center group cursor-pointer transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 75}ms` }}
                data-testid={`problem-card-${index}`}
              >
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:bg-[#2DD4BF] group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-lg">
                  <IconComponent className="w-7 h-7 text-[#1E4B6E] transition-colors group-hover:text-white" />
                </div>
                <h3 className="text-sm md:text-base font-semibold text-[#1E4B6E] transition-colors group-hover:text-[#2DD4BF]">
                  {problem.title}
                </h3>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Approach Section
const ApproachSection = () => {
  const [ref, isVisible] = useScrollAnimation();

  const features = [
    { icon: Users, text: "Equipo multidisciplinario coordinado" },
    { icon: Stethoscope, text: "Evaluación clínica exhaustiva" },
    { icon: HeartPulse, text: "Tratamiento basado en evidencia" },
    { icon: CheckCircle2, text: "Seguimiento personalizado" }
  ];

  return (
    <section data-testid="approach-section" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-[#1E4B6E] relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#2DD4BF]/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-2xl animate-float-delayed" />
      </div>

      <div className="max-w-7xl mx-auto relative" ref={ref}>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
          }`}>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Tratamos la causa, no solo el síntoma.
            </h2>
            <p className="text-white/85 text-lg leading-relaxed mb-6">
              En Be Ok Salud utilizamos un enfoque multidisciplinario donde kinesiólogos, nutricionista, médico, fonoaudiólogo y terapeutas trabajan de forma coordinada.
            </p>
            <p className="text-white/85 text-lg leading-relaxed">
              Cada paciente recibe una evaluación clínica profunda y un plan personalizado basado en evidencia científica.
            </p>
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            {features.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  className={`bg-white/10 backdrop-blur-sm p-5 rounded-xl flex items-center gap-4 group hover:bg-white/20 transition-all duration-500 cursor-default ${
                    isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-12 h-12 bg-[#2DD4BF] rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                    <IconComponent className="w-6 h-6 text-[#1E4B6E]" />
                  </div>
                  <span className="text-white font-medium group-hover:translate-x-1 transition-transform">{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <section id="servicios" data-testid="services-section" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto" ref={ref}>
        <div className={`text-center mb-16 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1E4B6E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Nuestros Servicios
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Ofrecemos una amplia gama de servicios de salud para cuidar de ti y tu familia.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            const isHovered = hoveredIndex === index;
            return (
              <div
                key={index}
                className={`service-card bg-white p-6 rounded-2xl border border-slate-100 shadow-sm relative overflow-hidden group transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                } ${isHovered ? 'shadow-xl border-[#2DD4BF]/30 -translate-y-2' : ''}`}
                style={{ transitionDelay: `${index * 50}ms` }}
                data-testid={`service-card-${index}`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Background gradient on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br from-[#2DD4BF]/5 to-transparent transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`} />
                
                <div className="relative">
                  <div className={`service-icon w-14 h-14 bg-[#F0F9FA] rounded-xl flex items-center justify-center mb-4 transition-all duration-300 ${isHovered ? 'bg-[#2DD4BF] scale-110 rotate-6' : ''}`}>
                    <IconComponent className={`w-7 h-7 transition-colors duration-300 ${isHovered ? 'text-white' : 'text-[#1E4B6E]'}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1E4B6E] mb-2 transition-colors group-hover:text-[#2DD4BF]">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {service.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className={`text-center mt-12 transition-all duration-700 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <a
            href={BOOKING_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2 group"
            data-testid="services-cta"
          >
            <span>Consultar disponibilidad</span>
            <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-2" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Team Section
const TeamSection = () => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section id="equipo" data-testid="team-section" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-[#F0F9FA]">
      <div className="max-w-7xl mx-auto" ref={ref}>
        <div className={`text-center mb-16 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1E4B6E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Equipo de Profesionales
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Contamos con especialistas certificados en diversas áreas de la salud.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {teamSpecialties.map((member, index) => {
            const IconComponent = member.icon;
            return (
              <div
                key={index}
                className={`team-card bg-white p-6 rounded-xl text-center group cursor-default transition-all duration-500 hover:shadow-xl hover:-translate-y-2 ${
                  isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
                }`}
                style={{ transitionDelay: `${index * 50}ms` }}
                data-testid={`team-card-${index}`}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#2DD4BF]/20 to-[#1E4B6E]/10 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110 group-hover:from-[#2DD4BF] group-hover:to-[#1E4B6E]">
                  <IconComponent className="w-8 h-8 text-[#1E4B6E] transition-colors duration-300 group-hover:text-white" />
                </div>
                <h3 className="text-sm font-semibold text-[#1E4B6E] leading-tight transition-colors group-hover:text-[#2DD4BF]">
                  {member.specialty}
                </h3>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const [activeIndex, setActiveIndex] = useState(null);

  return (
    <section data-testid="why-choose-section" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto" ref={ref}>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
          }`}>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E4B6E] mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
              ¿Por qué elegirnos?
            </h2>
            
            <div className="space-y-4">
              {whyChooseUs.map((item, index) => (
                <div 
                  key={index} 
                  className={`feature-check p-4 rounded-xl transition-all duration-300 cursor-default ${
                    activeIndex === index ? 'bg-[#F0F9FA] shadow-sm' : 'hover:bg-slate-50'
                  }`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                  style={{ transitionDelay: `${index * 75}ms` }}
                >
                  <CheckCircle2 className={`w-6 h-6 flex-shrink-0 transition-all duration-300 ${
                    activeIndex === index ? 'text-[#2DD4BF] scale-125 rotate-12' : 'text-[#2DD4BF]'
                  }`} />
                  <span className={`text-lg transition-colors ${
                    activeIndex === index ? 'text-[#1E4B6E] font-medium' : 'text-slate-700'
                  }`}>{item}</span>
                </div>
              ))}
            </div>

            <a
              href={BOOKING_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2 mt-8 group"
              data-testid="why-choose-cta"
            >
              <Phone className="w-5 h-5 transition-transform group-hover:rotate-12" />
              <span>Agenda tu evaluación</span>
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-2" />
            </a>
          </div>

          <div className={`bg-[#F0F9FA] p-8 md:p-12 rounded-3xl transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
          }`}>
            <div className="text-center">
              <div className="w-20 h-20 bg-[#2DD4BF] rounded-2xl flex items-center justify-center mx-auto mb-6 animate-pulse-subtle">
                <HeartPulse className="w-10 h-10 text-[#1E4B6E]" />
              </div>
              <h3 className="text-2xl font-bold text-[#1E4B6E] mb-4">
                Tu bienestar es nuestra prioridad
              </h3>
              <p className="text-slate-600">
                Nos comprometemos a brindarte la mejor atención con un enfoque humano y profesional.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Location Section
const LocationSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const [mapLoaded, setMapLoaded] = useState(false);

  return (
    <section id="ubicacion" data-testid="location-section" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-[#F0F9FA]">
      <div className="max-w-7xl mx-auto" ref={ref}>
        <div className={`text-center mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1E4B6E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Visítanos
          </h2>
          <p className="text-slate-600 text-lg">
            Centro Be Ok Salud
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Map */}
          <div className={`map-container aspect-video md:aspect-square relative overflow-hidden transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
          }`}>
            {!mapLoaded && (
              <div className="absolute inset-0 bg-slate-200 animate-pulse flex items-center justify-center">
                <MapPin className="w-12 h-12 text-slate-400 animate-bounce" />
              </div>
            )}
            <iframe
              src={MAPS_EMBED_URL}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ubicación Be Ok Salud"
              data-testid="google-map"
              onLoad={() => setMapLoaded(true)}
              className={`transition-opacity duration-500 ${mapLoaded ? 'opacity-100' : 'opacity-0'}`}
            />
          </div>

          {/* Info */}
          <div className={`bg-white p-8 rounded-2xl shadow-sm transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
          }`}>
            <div className="space-y-6">
              {/* Address */}
              <div className="flex items-start gap-4 group cursor-default">
                <div className="w-12 h-12 bg-[#2DD4BF]/10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:bg-[#2DD4BF] group-hover:scale-110">
                  <MapPin className="w-6 h-6 text-[#1E4B6E] transition-colors group-hover:text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1E4B6E] mb-1">Dirección</h3>
                  <p className="text-slate-600">
                    Avenida Einstein 290, Oficina 512<br />
                    Torre América<br />
                    Rancagua
                  </p>
                </div>
              </div>

              {/* Schedule */}
              <div className="flex items-start gap-4 group cursor-default">
                <div className="w-12 h-12 bg-[#2DD4BF]/10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:bg-[#2DD4BF] group-hover:scale-110">
                  <Clock className="w-6 h-6 text-[#1E4B6E] transition-colors group-hover:text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1E4B6E] mb-1">Horario de Atención</h3>
                  <p className="text-slate-600">
                    Lunes a Viernes<br />
                    10:00 – 13:00<br />
                    14:00 – 20:00
                  </p>
                </div>
              </div>

              {/* Contact */}
              <div className="flex items-start gap-4 group cursor-default">
                <div className="w-12 h-12 bg-[#2DD4BF]/10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:bg-[#2DD4BF] group-hover:scale-110">
                  <Phone className="w-6 h-6 text-[#1E4B6E] transition-colors group-hover:text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1E4B6E] mb-1">Contacto</h3>
                  <a 
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2DD4BF] hover:text-[#26b8a5] font-medium transition-colors"
                  >
                    +56 9 5675 5142
                  </a>
                </div>
              </div>

              <a
                href="https://maps.app.goo.gl/HqmfVFn6Bk1yLNsR7"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary w-full text-center flex items-center justify-center gap-2 group"
                data-testid="directions-btn"
              >
                <MapPin className="w-5 h-5 transition-transform group-hover:scale-110" />
                <span>Cómo llegar</span>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-2" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section id="contacto" data-testid="final-cta-section" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-[#1E4B6E] relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#2DD4BF]/10 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-2xl animate-float" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#2DD4BF]/10 rounded-full blur-xl animate-float-delayed" />
      </div>

      <div className="max-w-4xl mx-auto text-center relative" ref={ref}>
        <h2 className={`text-3xl md:text-4xl font-bold text-white mb-6 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`} style={{ fontFamily: 'Manrope, sans-serif' }}>
          Empieza hoy tu recuperación.
        </h2>
        <p className={`text-white/85 text-lg mb-10 max-w-2xl mx-auto transition-all duration-700 delay-100 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          Agenda una evaluación con nuestro equipo y comienza un tratamiento personalizado.
        </p>

        <div className={`flex flex-col sm:flex-row gap-4 justify-center transition-all duration-700 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <a
            href={BOOKING_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#2DD4BF] text-[#1E4B6E] font-semibold px-8 py-4 rounded-full hover:bg-[#26b8a5] transition-all duration-300 shadow-lg hover:shadow-xl text-lg flex items-center justify-center gap-2 group hover:-translate-y-1"
            data-testid="final-cta-primary"
          >
            <Phone className="w-5 h-5 transition-transform group-hover:rotate-12" />
            <span>Agendar Evaluación</span>
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-2" />
          </a>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#25D366] text-white font-semibold px-8 py-4 rounded-full hover:bg-[#20bd5a] transition-all duration-300 shadow-lg hover:shadow-xl text-lg flex items-center justify-center gap-2 group hover:-translate-y-1"
            data-testid="final-cta-whatsapp"
          >
            <MessageCircle className="w-5 h-5 transition-transform group-hover:scale-110" />
            <span>WhatsApp</span>
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-slate-900 text-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Logo & Description */}
          <div className="group">
            <img 
              src={LOGO_URL} 
              alt="Be Ok Salud Integral" 
              className="h-12 w-auto mb-4 transition-transform duration-300 group-hover:scale-105"
            />
            <p className="text-slate-400 text-sm">
              Centro de salud multidisciplinario en Rancagua especializado en dolor crónico, rehabilitación, nutrición y salud infantil.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4">Enlaces Rápidos</h4>
            <nav className="space-y-2">
              {[
                { href: "#servicios", label: "Servicios" },
                { href: "#equipo", label: "Equipo" },
                { href: "#ubicacion", label: "Ubicación" },
                { href: "#contacto", label: "Contacto" }
              ].map((link) => (
                <a 
                  key={link.href}
                  href={link.href} 
                  className="block text-slate-400 hover:text-[#2DD4BF] transition-all duration-300 text-sm hover:translate-x-1"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Contact & Social */}
          <div>
            <h4 className="font-semibold mb-4">Síguenos</h4>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-[#2DD4BF] transition-all duration-300 group"
              data-testid="instagram-link"
            >
              <Instagram className="w-5 h-5 transition-transform group-hover:scale-125 group-hover:rotate-12" />
              <span className="group-hover:translate-x-1 transition-transform">@be.ok.salud</span>
            </a>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Be Ok Salud Integral. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppFloatingButton = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowTooltip(true), 3000);
    const hideTimer = setTimeout(() => setShowTooltip(false), 8000);
    return () => {
      clearTimeout(timer);
      clearTimeout(hideTimer);
    };
  }, []);

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Tooltip */}
      <div className={`absolute bottom-full right-0 mb-3 bg-white text-[#1E4B6E] px-4 py-2 rounded-lg shadow-lg text-sm font-medium whitespace-nowrap transition-all duration-300 ${
        showTooltip || isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
      }`}>
        ¿Necesitas ayuda? ¡Escríbenos!
        <div className="absolute top-full right-6 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-white" />
      </div>

      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-btn p-4 rounded-full shadow-2xl flex items-center gap-2 animate-pulse-glow group"
        data-testid="whatsapp-floating-btn"
        aria-label="Contactar por WhatsApp"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <MessageCircle className={`w-7 h-7 transition-transform duration-300 ${isHovered ? 'rotate-12 scale-110' : ''}`} />
        <span className={`font-medium pr-2 overflow-hidden transition-all duration-300 ${
          isHovered ? 'max-w-32 opacity-100' : 'max-w-0 opacity-0 sm:max-w-32 sm:opacity-100'
        }`}>WhatsApp</span>
      </a>
    </div>
  );
};

// Main App Component
function App() {
  // Smooth scroll behavior
  useEffect(() => {
    const handleClick = (e) => {
      const href = e.target.getAttribute('href');
      if (href?.startsWith('#')) {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', handleClick);
    });

    return () => {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.removeEventListener('click', handleClick);
      });
    };
  }, []);

  return (
    <div className="App">
      <Header />
      <main>
        <HeroSection />
        <StatsSection />
        <ProblemsSection />
        <ApproachSection />
        <ServicesSection />
        <TeamSection />
        <WhyChooseUsSection />
        <LocationSection />
        <FinalCTASection />
      </main>
      <Footer />
      <WhatsAppFloatingButton />
    </div>
  );
}

export default App;
